/**
 * The guided first hour. Ten steps that tick off from real game state (server-side milestones, see
 * services/onboarding.ts), so progress survives sign-out and other devices. Skippable and
 * reopenable from the top bar / account page. Not a quest system: no rewards.
 * The matching contextual hints live in the panels where each step happens (guide/GuideHint).
 * The card sits on the LEFT (bottom-left on tablets/desktops, a bottom bar on phones) so it never covers the
 * right-hand system panel; it folds to a slim bar (step + title) and remembers that (guide-state). On phones
 * it publishes its height as --guide-h so the system sheet and the fleet action bar stack above it.
 */
import { useEffect, useRef, useState } from 'react';
import { ONBOARDING_GOALS, ONBOARDING_STEPS, type OnboardingGoal, type OnboardingStep } from '@galaxis/shared';
import { useGameStore } from '../../store/game-store.ts';
import { postOnboarding, postOnboardingGoal } from '../../api/onboarding.ts';
import { GUIDE_COPY, GOAL_COPY } from '../guide/steps.ts';

const TOTAL = ONBOARDING_STEPS.length;

export function OnboardingGuide({ homeSystemId }: { homeSystemId: string }) {
  const { showSystemOnMap, setActiveTab, setGuideOpen, onboarding: state, setOnboarding, activeTab, selectedSystemId } = useGameStore();
  const collapsed = useGameStore(s => s.guideCollapsed);
  const setGuideCollapsed = useGameStore(s => s.setGuideCollapsed);
  const [i, setI] = useState(0);
  const lastDone = useRef(-1);
  const rootRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  /** Phones only, without remembering: the player did not choose it, the panel they opened just needs the room */
  const fold = () => { if (window.innerWidth < 768) setGuideCollapsed(true); };

  // Jump to the next open step whenever one gets completed (or on the first read)
  const doneCount = state?.steps.filter(s => s.done).length ?? -1;
  useEffect(() => {
    if (!state || doneCount === lastDone.current) return;
    lastDone.current = doneCount;
    setI(state.completed ? TOTAL - 1 : Math.max(0, state.steps.findIndex(x => !x.done)));
  }, [state, doneCount]);

  // Phones: the card would cover the panel the player just opened — fold it away when the view changes or a system is picked
  const lastTab = useRef(activeTab);
  const lastSystem = useRef(selectedSystemId);
  useEffect(() => {
    const changed = lastTab.current !== activeTab || (lastSystem.current !== selectedSystemId && selectedSystemId !== null);
    lastTab.current = activeTab;
    lastSystem.current = selectedSystemId;
    if (changed && window.innerWidth < 768) setGuideCollapsed(true);
  }, [activeTab, selectedSystemId, setGuideCollapsed]);

  // Phones: tell the bottom sheets how much room the bar takes (cleared on tablets/desktops and when the guide goes away)
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const root = document.documentElement;
    const publish = () => {
      if (window.innerWidth < 768) root.style.setProperty('--guide-h', `calc(${Math.ceil(el.getBoundingClientRect().height) + 16}px + var(--sab, 0px))`);
      else root.style.removeProperty('--guide-h');
    };
    publish();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    ro?.observe(el);
    window.addEventListener('resize', publish);
    return () => { ro?.disconnect(); window.removeEventListener('resize', publish); root.style.removeProperty('--guide-h'); };
  }, []);

  const close = async () => {
    setGuideOpen(false);
    await postOnboarding('dismiss').then(setOnboarding).catch(() => undefined);
  };

  const pickGoal = async (goal: OnboardingGoal) => {
    const s = await postOnboardingGoal(goal).catch(() => null);
    if (s) setOnboarding(s);
    setActiveTab(GOAL_COPY[goal].tab);
  };

  const id: OnboardingStep = ONBOARDING_STEPS[i]!;
  const copy = GUIDE_COPY[id];
  const done = state?.steps.find(s => s.id === id)?.done ?? false;

  return (
    <section
      ref={rootRef}
      className="sci-fi-panel rounded-xl p-2 md:p-3 fixed z-40 flex flex-col shadow-xl max-h-[55dvh] md:max-h-[calc(100dvh-10rem)]
        left-[calc(0.5rem+var(--sal))] right-[calc(0.5rem+var(--sar))] bottom-[calc(0.5rem+var(--sab))]
        md:right-auto md:left-[calc(1rem+var(--sal))] md:bottom-[calc(1rem+var(--sab))] md:w-80 md:max-w-[calc(100vw-2rem)]"
      role="dialog" aria-label="Kezdő útmutató" data-testid="onboarding-guide" data-collapsed={collapsed ? 'true' : 'false'}
      onKeyDown={e => { if (e.key === 'Escape' && !collapsed) { e.stopPropagation(); setGuideCollapsed(true, true); toggleRef.current?.focus(); } }}
    >
      <div className="flex items-start gap-2 flex-shrink-0">
        <button ref={toggleRef} type="button" className="flex-1 min-w-0 text-left" style={{ minHeight: 44 }} aria-expanded={!collapsed}
          aria-label={collapsed ? 'Útmutató kinyitása' : 'Útmutató összecsukása'} data-testid="guide-toggle"
          onClick={() => setGuideCollapsed(!collapsed, true)}>
          <div className="text-[11px] uppercase tracking-wider text-slate-400">Első lépések · {i + 1}/{TOTAL} · <span data-testid="guide-progress">{Math.max(0, doneCount)}/{TOTAL} kész</span></div>
          <h2 className="text-sm font-semibold text-slate-100 mt-0.5 flex items-center gap-1.5 break-words [overflow-wrap:anywhere]">
            {done && <span aria-label="teljesítve" style={{ color: '#86efac' }}>✓</span>}{copy.title}
            <span aria-hidden className="ml-auto text-slate-400 flex-shrink-0">{collapsed ? '▴' : '▾'}</span>
          </h2>
        </button>
        <button type="button" className="btn-link" style={{ minHeight: 44, minWidth: 44 }} onClick={() => void close()}>{state?.completed ? 'Bezárás' : 'Kihagyás'}</button>
      </div>
      <ol className="flex gap-1 mt-1 flex-shrink-0" aria-label="Lépések állapota">
        {ONBOARDING_STEPS.map((s, k) => {
          const d = state?.steps.find(x => x.id === s)?.done ?? false;
          return <li key={s} data-testid={`guide-step-${s}`} data-done={d ? 'true' : 'false'} title={GUIDE_COPY[s].title}
            className="flex-1 h-1.5 rounded-full" style={{ background: d ? '#22c55e' : k === i ? '#4a9eff' : 'rgba(148,163,184,0.3)' }} />;
        })}
      </ol>
      <div className={collapsed ? 'hidden' : 'overflow-y-auto min-h-0 [overflow-wrap:anywhere]'} data-testid="guide-body">
        {state?.completed && id === 'goalChosen'
          ? <p className="text-xs text-slate-300 mt-2" data-testid="guide-complete">Minden kezdő lépés kész — jó utat a galaxisban!{state.goal ? ` Célod: ${GOAL_COPY[state.goal].label}.` : ''}</p>
          : <p className="text-xs text-slate-300 mt-2">{copy.text}</p>}
        {id === 'goalChosen' && (
          <div className="grid grid-cols-2 gap-2 mt-3" data-testid="guide-goals">
            {ONBOARDING_GOALS.map(g => (
              <button key={g} type="button" className="btn-secondary text-left" style={{ minHeight: 44, ...(state?.goal === g ? { borderColor: '#4a9eff', color: '#93c5fd' } : {}) }}
                data-testid={`guide-goal-${g}`} aria-pressed={state?.goal === g} onClick={() => void pickGoal(g)}>
                <span className="block text-xs font-semibold">{GOAL_COPY[g].label}</span>
                <span className="block text-[10px] text-slate-400">{GOAL_COPY[g].text}</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-3">
          {id === 'homeViewed' && <button type="button" className="btn-secondary" style={{ minHeight: 44 }} onClick={() => { showSystemOnMap(homeSystemId); fold(); }}>Megmutatom</button>}
          {(id === 'productionSeen' || id === 'energySeen' || id === 'firstUpgrade') && (
            <button type="button" className="btn-secondary" style={{ minHeight: 44 }} onClick={() => { setActiveTab('empire'); fold(); }}>Gazdaság megnyitása</button>
          )}
          {id === 'civTrace' && <button type="button" className="btn-secondary" style={{ minHeight: 44 }} onClick={() => { setActiveTab('diplomacy'); fold(); }}>Diplomácia megnyitása</button>}
          <span className="flex-1" />
          {i > 0 && <button type="button" className="btn-secondary" style={{ minHeight: 44 }} onClick={() => setI(i - 1)}>Vissza</button>}
          {i < TOTAL - 1 && <button type="button" className="btn-secondary" style={{ minHeight: 44, color: '#93c5fd', borderColor: 'rgba(74,158,255,0.5)' }} onClick={() => setI(i + 1)}>Tovább</button>}
        </div>
      </div>
    </section>
  );
}
